import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { Card } from 'react-bootstrap';
import { FlexRow, TableInMem, exportXlsModal } from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import gql from 'graphql-tag';
import { formatFloat } from 'shared-pages/utils/helpers';
import { Filters } from './filters';
import { useTranslation } from 'react-i18next';
export const gqlGetCollectedWeightByMaterial = gql`
  query getCollectedWeightByMaterial(
    $startDate: DateTime!
    $endDate: DateTime!
    $collectorId: Int
  ) {
    getCollectedWeightByMaterial(
      startDate: $startDate
      endDate: $endDate
      collectorId: $collectorId
    ) {
      data {
        materialId
        materialName
        totalWeight
        noRecords
      }
      recordCount
    }
  }
`;

export const CollectedWeightByMaterialPage = () => {
  const client = useApolloClient();
  const { t, i18n } = useTranslation();

  const [queryParams, setQueryParams] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59),
  });

  const [totalWeight, setTotalWeight] = useState(0);
  const [noRecords, setNoRecords] = useState(0);


  const [getData, { loading, data: dataRaw }] = useLazyQuery(gqlGetCollectedWeightByMaterial, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const totalWeight = data?.getCollectedWeightByMaterial?.data
        ?.map((item) => item.totalWeight || 0)
        .reduce((acc, weight) => acc + weight, 0);
      setTotalWeight(totalWeight);
      const noRecords = data?.getCollectedWeightByMaterial?.data
        ?.map((item) => item.noRecords || 0)
        .reduce((acc, value) => acc + value, 0);
      setNoRecords(noRecords);
    },
  });

  const data = dataRaw?.getCollectedWeightByMaterial?.data;
  const recordCount = dataRaw?.getCollectedWeightByMaterial?.recordCount;

  const filtersChanged = useCallback(
    (values) => {
      setQueryParams(values);
      getData({ variables: { ...values } });
    },
    [getData],
  );

  function useExcelGetQuery() {
    return useLazyQuery(gqlGetCollectedWeightByMaterial);
  }

  const handleExport = useCallback(async () => {
    const startDate = queryParams.startDate ? new Date(queryParams.startDate) : undefined;
    await exportXlsModal({
      apolloClient: client,
      getLazyQuery: useExcelGetQuery,
      filterVariables: queryParams,
      exportFileName: `Συλλεχθείσα Ποσότητα ${startDate
        ? startDate.getMonth() + 1 + '-' + startDate.getFullYear()
        : startDate.getFullYear()
        }`,
      columnsDef: [
        { fieldName: 'materialName', fieldTitle: 'Απόβλητο', fieldType: 'TEXT' },
        { fieldName: 'totalWeight', fieldTitle: 'Συνολικό Βάρος (kg)', fieldType: 'FLOAT' },
        { fieldName: 'noRecords', fieldTitle: 'Πλήθος Εντύπων', fieldType: 'FLOAT' },
      ],
    });
  }, [queryParams]);

  const columns = useMemo(
    () => [
      {
        accessor: 'materialName',
        Header: 'Απόβλητο',
      },
      {
        accessor: 'totalWeight',
        Header: 'Συνολικό Βάρος (kg)',
        Cell: ({ row }) => (
          <div className="text-center">{formatFloat(row.original.totalWeight, 2)}</div>
        ),
        Footer: () => <div className="text-center">{formatFloat(totalWeight, 2)}</div>,
      },
      {
        accessor: 'noRecords',
        Header: 'Πλήθος Εντύπων',
        Cell: ({ row }) => (
          <div className="text-center">{formatFloat(row.original.noRecords, 2)}</div>
        ),
        Footer: () => <div className="text-center">{formatFloat(noRecords, 2)}</div>,
      },

    ],
    [totalWeight, noRecords],
  );

  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">{t('Συλλεχθείσα Ποσότητα ανά Κατηγορία')}</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        <Filters onChange={filtersChanged} initialValues={queryParams} />
        <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
          {data ? (
            <TableInMem
              onExport={handleExport}
              columns={columns}
              data={data}
              hasSearch={false}
              hasFooter={true}
              propPageSize={50}
            />
          ) : (
            <div className="empty-table-container"></div>
          )}
        </LoadingOverlay>
      </Card.Body>
    </Card>
  );
};

